/**
 * Consumer-Skida cross-check.
 *
 * Consumer Skida and Skida PRO share one Firestore database, so this is a plain
 * same-project query against `users` — no Cloud Function, no second service
 * account, no cross-project hop.
 *
 * This is the ONE place the CRM reads a collection it does not own. It needs a
 * read rule on `users` scoped to approved CRM users — block 3 of
 * firestore.rules.crm.snippet. Everything else stays inside `skidaCrm*`.
 *
 * Matching is on the `users.email` field. Note this is a case-sensitive
 * equality match: a user document whose stored email is not lowercased will not
 * be found, and reads as "New to Skida". Every address seen so far is stored
 * lowercased, so this is a latent edge case rather than a live problem.
 *
 * Deliberately not persisted onto the lead: per the handoff this is a derived,
 * read-only signal, and a stored copy would go stale the moment a contact signs
 * up. Memoised for the lifetime of the browser session only.
 */
import { collection, getDocs, query, where } from 'firebase/firestore'
import { productDb } from '@/shared/firebase/client'
import { PRODUCT_COLLECTIONS } from '@/shared/firebase/collections'
import type { ConsumerAwareness } from '@/types/crm'
import { toIsoOrNull } from '@/shared/util/dates'

/** Firestore `in` queries accept at most 30 values. */
const IN_CHUNK = 30

/** Session cache: lowercased email → awareness. */
const cache = new Map<string, ConsumerAwareness>()

/**
 * Signup date. `users.createdAt` is epoch milliseconds (confirmed against the
 * live schema); `toIsoOrNull` also copes with the Timestamp and ISO-string
 * variants other documents in this database use. A missing date only drops the
 * year from the "Skida user since {year}" pill — it never changes the boolean.
 */
function readSince(data: Record<string, unknown>): string | undefined {
  return toIsoOrNull(data.createdAt) ?? undefined
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

/**
 * Look up a batch of contact emails against the consumer `users` collection.
 * Already-known emails are served from the session cache; only the remainder
 * hit Firestore.
 */
export async function lookupAwareness(emails: string[]): Promise<Map<string, ConsumerAwareness>> {
  const normalised = [...new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean))]
  const missing = normalised.filter((e) => !cache.has(e))

  if (missing.length > 0) {
    try {
      const found = new Map<string, string | undefined>()

      await Promise.all(
        chunk(missing, IN_CHUNK).map(async (batch) => {
          const snap = await getDocs(
            query(collection(productDb, PRODUCT_COLLECTIONS.users), where('email', 'in', batch)),
          )
          for (const doc of snap.docs) {
            const data = doc.data() as Record<string, unknown>
            const email = String(data.email ?? '')
              .trim()
              .toLowerCase()
            if (email) found.set(email, readSince(data))
          }
        }),
      )

      for (const email of missing) {
        cache.set(email, found.has(email)
          ? { email, isConsumerUser: true, since: found.get(email) }
          : { email, isConsumerUser: false })
      }
    } catch (err) {
      // A rules rejection or outage must not break the workspace. Degrade to
      // "unknown" — which renders like "New to Skida" — but do not cache the
      // failure, so the next selection retries.
      console.warn('[crm] awareness lookup failed, treating contacts as unknown', err)
      const out = new Map<string, ConsumerAwareness>()
      for (const email of normalised) {
        out.set(email, cache.get(email) ?? { email, isConsumerUser: false })
      }
      return out
    }
  }

  const out = new Map<string, ConsumerAwareness>()
  for (const email of normalised) {
    const hit = cache.get(email)
    if (hit) out.set(email, hit)
  }
  return out
}
